import React, { useEffect, useState } from 'react';
import {
  Box,
  Stack,
  Grid,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { ArrowLeft, Search, Plus, Pencil, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { appointmentFlowApi } from '../../../api';
import useUIStore from '../../../store/useUIStore';
import useConfirm from '../../../hooks/useConfirm';

const emptyForm = {
  name: '',
  durationMinutes: '',
  status: 'active',
};

const statusLabel = (value) => (value === 1 || value === 'active' ? 'active' : 'inactive');

const ServicesPage = () => {
  const { showError } = useUIStore();
  const { confirm } = useConfirm();
  const [services, setServices] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const rowsPerPage = 10;

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, services]);

  const fetchServices = async () => {
    try {
      const response = await appointmentFlowApi.getServices();
      const data = response?.data?.data || response?.data || [];
      setServices(Array.isArray(data) ? data : []);
    } catch (error) {
      setServices([]);
    }
  };

  const applyFilters = () => {
    let filtered = services;
    const search = searchTerm.toLowerCase();

    if (search) {
      filtered = filtered.filter((item) => (item.name || '').toLowerCase().includes(search));
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    try {
      await appointmentFlowApi.createService({
        name: formData.name,
        durationMinutes: formData.durationMinutes,
        status: formData.status,
      });
      setShowAddModal(false);
      resetForm();
      fetchServices();
    } catch (error) {
      showError(error?.response?.data?.message || 'Failed to add service');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await appointmentFlowApi.updateService(formData.id, {
        name: formData.name,
        durationMinutes: formData.durationMinutes,
        status: formData.status,
      });
      setShowEditModal(false);
      resetForm();
      fetchServices();
    } catch (error) {
      showError(error?.response?.data?.message || 'Failed to update service');
    }
  };

  const handleEdit = (service) => {
    setFormData({
      id: service.id,
      name: service.name || '',
      durationMinutes: service.durationMinutes || '',
      status: statusLabel(service.status),
    });
    setShowEditModal(true);
  };

  const handleDelete = async (serviceId) => {
    const isConfirmed = await confirm({
      title: 'Delete Service',
      message: 'Are you sure you want to delete this service?',
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'error',
    });
    if (!isConfirmed) return;

    try {
      await appointmentFlowApi.deleteService(serviceId);
      fetchServices();
    } catch (error) {
      showError(error?.response?.data?.message || 'Failed to delete service');
    }
  };

  const handleStatusToggle = async (serviceId, currentStatus) => {
    const nextStatus = statusLabel(currentStatus) === 'active' ? 'inactive' : 'active';
    try {
      await appointmentFlowApi.updateService(serviceId, { status: nextStatus });
      fetchServices();
    } catch (error) {
      showError(error?.response?.data?.message || 'Failed to update status');
    }
  };

  const resetForm = () => {
    setFormData(emptyForm);
  };

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentData = filteredData.slice(startIndex, startIndex + rowsPerPage);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <Button variant="text" startIcon={<ArrowLeft />} href="/appointments" sx={{ minWidth: 0, px: 0 }}>
          Back
        </Button>
        <Typography variant="h5">Services Management</Typography>
      </Stack>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
              <Search size={16} />
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Search services..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Stack direction="row" justifyContent="flex-start" mb={2}>
        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={() => {
            resetForm();
            setShowAddModal(true);
          }}
        >
          Add New Service
        </Button>
      </Stack>

      <Card>
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>S.No</TableCell>
                  <TableCell>Service Name</TableCell>
                  <TableCell>Duration (min)</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No services found
                    </TableCell>
                  </TableRow>
                ) : (
                  currentData.map((service, index) => (
                    <TableRow key={service.id} hover>
                      <TableCell>{startIndex + index + 1}</TableCell>
                      <TableCell>{service.name || '-'}</TableCell>
                      <TableCell>{service.durationMinutes || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={statusLabel(service.status)}
                          size="small"
                          sx={{
                            bgcolor: statusLabel(service.status) === 'active' ? 'success.light' : 'error.light',
                            color: statusLabel(service.status) === 'active' ? 'success.contrastText' : 'error.contrastText',
                            textTransform: 'capitalize',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Pencil size={14} />}
                            onClick={() => handleEdit(service)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<Trash2 size={14} />}
                            onClick={() => handleDelete(service.id)}
                          >
                            Delete
                          </Button>
                          {statusLabel(service.status) === 'active' ? (
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<ToggleRight size={16} />}
                              onClick={() => handleStatusToggle(service.id, service.status)}
                            >
                              Active
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              variant="contained"
                              color="inherit"
                              startIcon={<ToggleLeft size={16} />}
                              onClick={() => handleStatusToggle(service.id, service.status)}
                            >
                              Inactive
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} mt={3}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Typography variant="body2">
                Page {currentPage} of {totalPages}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Dialog open={showAddModal} onClose={() => setShowAddModal(false)} fullWidth maxWidth="md">
        <DialogTitle>Add New Service</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Service Name"
                name="name"
                fullWidth
                required
                value={formData.name}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Duration (minutes)"
                name="durationMinutes"
                type="number"
                fullWidth
                value={formData.durationMinutes}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select name="status" label="Status" value={formData.status} onChange={handleInputChange}>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddModal(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddSubmit}>
            Add Service
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showEditModal} onClose={() => setShowEditModal(false)} fullWidth maxWidth="md">
        <DialogTitle>Edit Service</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Service Name"
                name="name"
                fullWidth
                required
                value={formData.name}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Duration (minutes)"
                name="durationMinutes"
                type="number"
                fullWidth
                value={formData.durationMinutes}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select name="status" label="Status" value={formData.status} onChange={handleInputChange}>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditModal(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSubmit}>
            Update Service
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServicesPage;
